import { useEffect } from 'react'
import { Form } from 'react-bootstrap'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import DefaultDatePicker from '../datepicker/DefaultDatePicker'
import { RequiredAsterisk } from '../requiredasterisk'
import { ibanFormat } from '../utils'
import { fieldParams } from './personmodalfieldparams'

export const Nr = ({ onChange, defaultValue, formErrors, disabled }) => {
	const { t } = useTranslation()

	return (
		<>
			<Form.Label>{t('PersonNumber', 'Henkilönumero')}</Form.Label>
			<Form.Control
				type="number"
				placeholder={t('PersonNumberPlaceholder', '123')}
				onChange={onChange}
				defaultValue={defaultValue}
				isInvalid={formErrors && !!formErrors.nr}
				min={fieldParams.nr?.minValue}
				max={fieldParams.nr?.maxValue}
				disabled={disabled}
			/>
			<Form.Control.Feedback type="invalid">{formErrors?.nr}</Form.Control.Feedback>
		</>
	)
}

export const CallingName = ({ onChange, defaultValue, formErrors, disabled }) => {
	const { t } = useTranslation()

	return (
		<>
			<Form.Label>{t('CallingName', 'Kutsumanimi')}</Form.Label>
			<Form.Control
				onChange={onChange}
				defaultValue={defaultValue}
				isInvalid={formErrors && !!formErrors.callingName}
				maxLength={fieldParams.callingName?.maxLength}
				disabled={disabled}
			/>
			<Form.Control.Feedback type="invalid">{formErrors?.callingName}</Form.Control.Feedback>
		</>
	)
}

export const Forename = ({ onChange, defaultValue, formErrors, disabled, surname }) => {
	const { t } = useTranslation()
	const { register, setValue } = useFormContext() // retrieve all hook methods
	setValue('forename', defaultValue)

	return (
		<>
			<Form.Label>
				{t('Forename', 'Etunimi')}
				<RequiredAsterisk />
			</Form.Label>
			<Form.Control
				placeholder={t('Forename', 'Etunimi')}
				defaultValue={defaultValue}
				isInvalid={formErrors && !!formErrors.forename}
				maxLength={fieldParams.forename?.maxLength}
				disabled={disabled}
				{...register('forename', {
					onChange: (e) => {
						setValue('name', fullName(e.target.value, surname))
						onChange(e)
					},
					shouldUnregister: true,
				})}
			/>
			<Form.Control.Feedback type="invalid">{formErrors?.forename}</Form.Control.Feedback>
		</>
	)
}

export const Surname = ({ onChange, defaultValue, formErrors, disabled, forename }) => {
	const { t } = useTranslation()
	const { register, setValue } = useFormContext()
	setValue('surname', defaultValue)

	return (
		<>
			<Form.Label>
				{t('Surname', 'Sukunimi')}
				<RequiredAsterisk />
			</Form.Label>
			<Form.Control
				placeholder={t('Surname', 'Sukunimi')}
				defaultValue={defaultValue}
				isInvalid={formErrors && !!formErrors.surname}
				maxLength={fieldParams.surname?.maxLength}
				disabled={disabled}
				{...register('surname', {
					onChange: (e) => {
						setValue('name', fullName(forename, e.target.value))
						onChange(e)
					},
					shouldUnregister: true,
				})}
			/>
			<Form.Control.Feedback type="invalid">{formErrors?.surname}</Form.Control.Feedback>
		</>
	)
}

const fullName = (forename, surname) => `${surname ? surname.trim() : ''} ${forename ? forename.trim() : ''}`

let isNameSet = 0
export const Name = ({ onChange, defaultValue, formErrors, disabled }) => {
	const { t } = useTranslation()
	const { register, setValue } = useFormContext() // retrieve all hook methods

	// works differently in development and production for some reason
	if (++isNameSet < 3) {
		// setValue is called multiple times, so we need to check if it's the first time
		setValue('name', defaultValue)
	}

	useEffect(() => {
		return () => {
			isNameSet = 0
		}
	}, [])

	return (
		<>
			<Form.Label>{t('Name', 'Nimi')}</Form.Label>
			<Form.Control
				onChange={onChange}
				defaultValue={defaultValue}
				isInvalid={formErrors && !!formErrors.name}
				maxLength={fieldParams.name?.maxLength}
				disabled={disabled}
				{...register('name')}
			/>
			<Form.Control.Feedback type="invalid">{formErrors?.name}</Form.Control.Feedback>
		</>
	)
}

export const IdentifierCode = ({ onChange, defaultValue, formErrors, disabled }) => {
	const { t } = useTranslation()

	return (
		<>
			<Form.Label>{t('SocSec', 'Yksilöintitunnus')}</Form.Label>
			<Form.Control
				placeholder="XXXXXX-XXXX"
				onChange={onChange}
				defaultValue={defaultValue}
				isInvalid={formErrors && !!formErrors.socSec}
				maxLength={fieldParams.socSec?.maxLength}
				disabled={disabled}
			/>
			<Form.Control.Feedback type="invalid">{formErrors?.socSec}</Form.Control.Feedback>
		</>
	)
}

export const IdentifierCodeType = ({ onChange, defaultValue, disabled }) => {
	const { t } = useTranslation()
	const { register, setValue } = useFormContext() // retrieve all hook methods
	setValue('gender', defaultValue)

	const genderMap = {
		M: t('Male', 'Mies'),
		N: t('Female', 'Nainen'),
		O: t('LimitedCompany', 'Osakeyhtiö'),
		Y: t('BusinessId', 'Y-tunnus'),
		K: t('TemporaryId', 'Tilapäinen yksilöintitunnus'),
	}

	return (
		<>
			<Form.Label>{t('IdentifierCodeType', 'Tunnuksen tyyppi')}</Form.Label>
			<Form.Select
				disabled={disabled}
				defaultValue={defaultValue}
				{...register('gender', {
					onChange: onChange,
					shouldUnregister: true,
				})}
			>
				{Object.entries(genderMap).map((i) => (
					<option key={i[0]} value={i[0]}>
						{i[1]}
					</option>
				))}
			</Form.Select>
		</>
	)
}

export const Tel = ({ onChange, defaultValue, formErrors, disabled }) => {
	const { t } = useTranslation()

	return (
		<>
			<Form.Label>{t('PhoneNumber1', 'Puhelinnumero työ')}</Form.Label>
			<Form.Control
				type="tel"
				onChange={onChange}
				defaultValue={defaultValue}
				isInvalid={formErrors && !!formErrors.tel}
				maxLength={fieldParams.tel?.maxLength}
				disabled={disabled}
			/>
			<Form.Control.Feedback type="invalid">{formErrors?.tel}</Form.Control.Feedback>
		</>
	)
}

export const Tel2 = ({ onChange, defaultValue, formErrors, disabled }) => {
	const { t } = useTranslation()

	return (
		<>
			<Form.Label>{t('PhoneNumber2', 'Puhelinnumero koti')}</Form.Label>
			<Form.Control
				type="tel"
				onChange={onChange}
				defaultValue={defaultValue}
				isInvalid={formErrors && !!formErrors.tel2}
				maxLength={fieldParams.tel2?.maxLength}
				disabled={disabled}
			/>
			<Form.Control.Feedback type="invalid">{formErrors?.tel2}</Form.Control.Feedback>
		</>
	)
}

export const StreetAddress = ({ onChange, defaultValue, formErrors, disabled }) => {
	const { t } = useTranslation()

	return (
		<>
			<Form.Label>{t('StreetAddress', 'katuosoite')}</Form.Label>
			<Form.Control
				onChange={onChange}
				defaultValue={defaultValue}
				isInvalid={formErrors && !!formErrors.streetAddress}
				maxLength={fieldParams.streetAddress?.maxLength}
				disabled={disabled}
			/>
			<Form.Control.Feedback type="invalid">{formErrors?.streetAddress}</Form.Control.Feedback>
		</>
	)
}

export const PostalCode = ({ onChange, defaultValue, formErrors, disabled }) => {
	const { t } = useTranslation()

	return (
		<>
			<Form.Label>{t('PostNumber', 'Postinumero')}</Form.Label>
			<Form.Control
				onChange={onChange}
				defaultValue={defaultValue}
				isInvalid={formErrors && !!formErrors.postalCode}
				maxLength={fieldParams.postalCode?.maxLength}
				disabled={disabled}
			/>
			<Form.Control.Feedback type="invalid">{formErrors?.postalCode}</Form.Control.Feedback>
		</>
	)
}

export const PostalAddress = ({ onChange, defaultValue, formErrors, disabled }) => {
	const { t } = useTranslation()

	return (
		<>
			<Form.Label>{t('PostalAddress', 'Postitoimipaikka')}</Form.Label>
			<Form.Control
				onChange={onChange}
				defaultValue={defaultValue}
				isInvalid={formErrors && !!formErrors.postalAddress}
				maxLength={fieldParams.postalAddress?.maxLength}
				disabled={disabled}
			/>
			<Form.Control.Feedback type="invalid">{formErrors?.postalAddress}</Form.Control.Feedback>
		</>
	)
}

export const Country = ({ onChange, defaultValue, formErrors, disabled }) => {
	const { t } = useTranslation()

	return (
		<>
			<Form.Label>{t('CountryCode', 'Maakoodi')}</Form.Label>
			<Form.Control
				placeholder={t('CountryCodePlaceholder', 'FI')}
				onChange={onChange}
				defaultValue={defaultValue}
				isInvalid={formErrors && !!formErrors.country}
				maxLength={fieldParams.country?.maxLength}
				disabled={disabled}
			/>
			<Form.Control.Feedback type="invalid">{formErrors?.country}</Form.Control.Feedback>
		</>
	)
}

export const IBAN = ({ onChange, defaultValue, formErrors, disabled }) => {
	const { t } = useTranslation()

	return (
		<>
			<Form.Label>{t('Iban', 'Tilinumero (IBAN)')}</Form.Label>
			<Form.Control
				onChange={onChange}
				defaultValue={ibanFormat(defaultValue)}
				isInvalid={formErrors && !!formErrors.iban}
				maxLength={fieldParams.iban?.maxLength}
				disabled={disabled}
			/>
			<Form.Control.Feedback type="invalid">{formErrors?.iban}</Form.Control.Feedback>
		</>
	)
}

export const BIC = ({ onChange, defaultValue, formErrors, jsonProperty, disabled }) => {
	const { t } = useTranslation()
	const { register, setValue } = useFormContext() // retrieve all hook methods
	setValue(jsonProperty, defaultValue)

	return (
		<>
			<Form.Label>{t('BIC', 'BIC')}</Form.Label>
			<Form.Control
				defaultValue={defaultValue}
				isInvalid={formErrors && !!formErrors.bic}
				maxLength={fieldParams.bic?.maxLength}
				disabled={disabled}
				{...register(jsonProperty, {
					onChange: onChange,
					shouldUnregister: true,
				})}
			/>
			<Form.Control.Feedback type="invalid">{formErrors?.bic}</Form.Control.Feedback>
		</>
	)
}

export const StartDate = ({ onChange, value, formErrors, disabled }) => {
	const { t } = useTranslation()
	const prefix = 'personStartDate'

	return (
		<DefaultDatePicker
			id={`${prefix}-start`}
			name="startDate"
			label={t('StartDate', 'Työsuhteen aloituspäivä')}
			value={value}
			onChange={onChange}
			disabled={disabled}
			error={formErrors?.startDate}
		/>
	)
}
